import { computed, shallowRef, watch } from "vue";
import { defineStore } from "pinia";
import { gcCommentStore } from "../editor/commentMutations";
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import { canPersistProjectRoot } from "../lib/platform";
import { dispatchInto } from "../lib/tea";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { saveActive, saveAs } from "../lib/tauri/core";
import { isManuscriptTabFile } from "../lib/zwConfig";
import { isForeignJsonDocBody } from "../lib/zwdoc/format";
import { applyPayload } from "./editorPayload";
import { useEditorCloudStore } from "./useEditorCloudStore";
import { useEditorHeadingsStore } from "./useEditorHeadingsStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useProjectGoalsStore } from "./useProjectGoalsStore";
import { useSceneOpeningsStore } from "./useSceneOpeningsStore";
import { useTimelineMapsStore } from "./useTimelineMapsStore";
import {
  initialEditorSaveModel,
  isSaving,
  flushDecision,
  shouldDrain,
  updateEditorSave,
  writeOutcome,
  type EditorSaveCmd,
  type EditorSaveMsg,
  type SaveFacts,
} from "./useEditorSaveStore.update";

const AUTOSAVE_DELAY_MS = 1000;

/**
 * Save / autosave for the open file.
 *
 * TEA: pure Model/Msg/Cmd in `useEditorSaveStore.update.ts`. This shell reads
 * session facts into Msgs and interprets Cmds (`saveActive`, `saveAs`, the
 * autosave timer, cloud push). Mutators that return nothing take effect
 * synchronously; `flushSave` still shares one in-flight promise.
 */
export const useEditorSaveStore = defineStore("editorSave", () => {
  const session = useEditorSessionStore();
  const headings = useEditorHeadingsStore();
  const goals = useProjectGoalsStore();

  const model = shallowRef(initialEditorSaveModel);
  /** Effect handle for the in-flight write loop. Shared by every caller. */
  let pendingSave: Maybe<Promise<boolean>> = nothing;
  const autosaveTimer = createTimeoutHandle();

  const saving = computed(() => isSaving(model.value));

  function facts(): SaveFacts {
    return {
      hasActivePath: isJust(session.activePath),
      dirty: session.dirty,
      canPersist: canPersistProjectRoot(session.rootPath),
    };
  }

  /** Drain edits made while an earlier snapshot was being written. All
   * callers share this promise, so navigation waits for the latest draft. */
  async function writeLoop(): Promise<boolean> {
    try {
      while (shouldDrain(session.dirty, isJust(session.activePath))) {
        const active = session.activePath;
        if (isNothing(active)) break;
        const path = active.value;
        const content = session.doc;
        const synopsis = session.synopsis;
        // Mirror Rust `save_active`: never write map/other JSON into `.zwdoc`.
        if (
          path.toLowerCase().endsWith(".zwdoc") &&
          isForeignJsonDocBody(content)
        ) {
          console.error("Autosave refused: non-.zwdoc JSON buffer for", path);
          return false;
        }
        await saveActive(content, synopsis);
        const outcome = writeOutcome(
          { path, content, synopsis },
          {
            activePath: session.activePath,
            doc: session.doc,
            synopsis: session.synopsis,
          },
        );
        if (outcome.kind === "abandoned") return false;
        session.dirty = outcome.stillDirty;
        const rel = session.activeBasename;
        if (isJust(rel) && session.activeMap === false) {
          goals.noteFileFlushed(rel.value, content);
        }
        // The snapshot matches the buffer, so the outline/timeline cache can
        // keep it. A newer draft stays on the live buffer watch instead.
        if (outcome.stillDirty === false && isJust(rel)) {
          if (session.activeMap && isJust(session.activeDatabase)) {
            void useTimelineMapsStore().noteMapSaved(
              session.activeDatabase.value,
              content,
            );
          } else if (isManuscriptTabFile(rel)) {
            useSceneOpeningsStore().noteSaved(rel.value, content);
          }
        }
      }
      void headings.refreshEntityOccurrences();
      goals.scheduleChronicleRefresh();
      return true;
    } catch (error) {
      console.error("Autosave failed", error);
      return false;
    } finally {
      pendingSave = nothing;
      void send({ type: "FlushFinished" });
    }
  }

  async function runSaveAs(): Promise<void> {
    try {
      const result = await saveAs(session.doc);
      if (result !== null) {
        applyPayload(result, { keepDoc: true });
        session.dirty = false;
        if (result.kind === "project") {
          await headings.refreshEntityOccurrences();
          await useEditorCloudStore().pushImmediateCloudSync();
        }
      }
    } catch (error) {
      console.error("Save As failed", error);
    } finally {
      void send({ type: "SaveAsFinished" });
    }
  }

  async function runCmd(cmd: EditorSaveCmd): Promise<void> {
    switch (cmd.kind) {
      case "cancelAutosave":
        autosaveTimer.clear();
        return;
      case "scheduleAutosave":
        autosaveTimer.schedule(AUTOSAVE_DELAY_MS, () => {
          void flushSave();
        });
        return;
      case "runFlush": {
        // Set before any await so a second synchronous caller joins it.
        const run = writeLoop();
        pendingSave = just(run);
        await run;
        return;
      }
      case "runSaveAs":
        await runSaveAs();
        return;
      case "requestSaveAs":
        await send({
          type: "SaveAsRequested",
          canPersist: canPersistProjectRoot(session.rootPath),
        });
        return;
      case "setSuppressDirty":
        session.suppressDirty = cmd.value;
        return;
      case "setDoc":
        session.doc = cmd.value;
        return;
      case "setDirty":
        session.dirty = cmd.value;
        return;
      case "refreshLexicon":
        headings.scheduleHarperLexiconRefresh();
        return;
      case "restoreVersion":
        session.suppressDirty = true;
        session.doc = cmd.content;
        session.dirty = true;
        session.suppressDirty = false;
        await flushSave();
        void headings.refreshEntityOccurrences();
        return;
      case "markDirtyAndFlush": {
        session.dirty = true;
        const ok = await flushSave();
        await send({ type: "SaveFlushed", ok });
        return;
      }
      case "pushCloud":
        try {
          await useEditorCloudStore().pushImmediateCloudSync();
        } catch (error) {
          console.error("Cloud sync failed", error);
        }
        return;
      case "flushForBlur":
        await flushSave();
        await send({ type: "BlurFlushed" });
        return;
      case "blurSyncCloud":
        await useEditorCloudStore().onBlurSync();
        return;
      default:
        assertNever(cmd);
    }
  }

  function send(msg: EditorSaveMsg): Promise<void> {
    return dispatchInto(model, msg, updateEditorSave, runCmd);
  }

  async function flushSave(): Promise<boolean> {
    const current = facts();
    const decision = flushDecision(model.value, current);
    switch (decision) {
      case "join":
        return isJust(pendingSave) ? pendingSave.value : true;
      case "noop":
        return true;
      case "refuse":
        return false;
      case "start":
        // `runFlush` registers `pendingSave` synchronously inside `send`.
        void send({ type: "FlushRequested", facts: current });
        return isJust(pendingSave) ? pendingSave.value : false;
      default:
        return assertNever(decision);
    }
  }

  function cancelAutosave(): void {
    void send({ type: "CancelAutosave" });
  }

  // Watches `doc` and `synopsis` together (not two separate `watch()` calls) so a
  // load that sets both at once (`applyPayload`) only runs this body once — two
  // independent watchers would each try to consume the same `suppressDirty` flag,
  // and whichever fired second would see it already cleared and wrongly mark the
  // freshly-loaded file dirty.
  watch(
    () => [session.doc, session.synopsis] as const,
    ([value]) => {
      const activeMap = session.activeMap;
      const current = facts();
      void send({
        type: "DocChanged",
        value,
        cleaned: activeMap ? value : gcCommentStore(value),
        activeMap,
        suppressDirty: session.suppressDirty,
        hasActiveDatabase: isJust(session.activeDatabase),
        canSchedule: current.canPersist && current.hasActivePath,
      });
    },
  );

  async function onRestoreVersion(content: string): Promise<void> {
    await send({ type: "RestoreRequested", content, facts: facts() });
  }

  async function onSave(): Promise<void> {
    await send({ type: "SaveRequested", facts: facts() });
  }

  async function onEditorBlur(): Promise<void> {
    await send({ type: "BlurRequested" });
  }

  function dispose(): void {
    cancelAutosave();
  }

  return {
    saving,
    flushSave,
    cancelAutosave,
    onSave,
    onRestoreVersion,
    onEditorBlur,
    dispose,
  };
});
